import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
} from "react";

type AdsStatus = "loading" | "ready" | "error";

interface AdUnitsConfig {
  banner?: boolean;
  billboard?: boolean;
  largeLeaderboard?: boolean;
  largeSkyscraper?: boolean;
  mediumRectangle?: boolean;
  mobileLeaderboard?: boolean;
  videoSlateSingle?: boolean;
  playerTopLeftBug?: boolean;
}

interface AdsConfig {
  video:
    | { enabled: false }
    | {
        enabled: true;
        networkId: string;
        adUnit: string;
        size: string;
      };
  display:
    | { enabled: false }
    | {
        enabled: true;
        networkId: string;
        adUnits: AdUnitsConfig;
        expectNoFill: boolean;
      };
}

interface AdsContextValue {
  imaStatus: AdsStatus;
  ima: object | null;
  gptStatus: AdsStatus;
  gpt: typeof window.googletag | null;
}

// Partners with display ads enabled and their allowed ad units
const displayAdPartners: Record<string, AdUnitsConfig> = {
  "HLTV.ORG": {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
    playerTopLeftBug: true,
  },
  HLTV: {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
    playerTopLeftBug: true,
  },
  "CROM, INC.": {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
    playerTopLeftBug: true,
  },
  "CROM,INC": {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
    playerTopLeftBug: true,
  },
  "U.GG": {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
  },
  FACEIT: {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
  },
  L202211: {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
  },
  GC2022: {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
  },
  ESPLAY: {
    banner: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
  },
  REFRAG: {
    banner: true,
    mobileLeaderboard: true,
  },
  // Website (ALLSTAR) - enable all ad units for first-party traffic
  ALLSTAR: {
    banner: true,
    billboard: true,
    largeLeaderboard: true,
    largeSkyscraper: true,
    mediumRectangle: true,
    mobileLeaderboard: true,
    videoSlateSingle: true,
    playerTopLeftBug: true,
  },
};

const displayAdNoFillExemptPartners = new Set(["ALLSTAR", "HLTV", "HLTV.ORG"]);

const getDisplayConfig = (
  platform: string,
):
  | { enabled: false }
  | {
      enabled: true;
      networkId: string;
      adUnits: AdUnitsConfig;
      expectNoFill: boolean;
    } => {
  const partnerConfig = displayAdPartners[platform];

  if (!partnerConfig) {
    return { enabled: false };
  }

  return {
    enabled: true,
    networkId: "23325224050",
    adUnits: partnerConfig,
    expectNoFill: !displayAdNoFillExemptPartners.has(platform),
  };
};

const getVideoConfig = (
  platform: string,
  queryParams?: URLSearchParams,
):
  | { enabled: false }
  | { enabled: true; networkId: string; adUnit: string; size: string } => {
  // HLTV video ads use their network
  if (["HLTV", "HLTV.ORG", "CROM, INC.", "CROM,INC"].includes(platform)) {
    const baseConfig = {
      enabled: true as const,
      networkId: "63317524",
      adUnit: "hltv/hltv_geo1_video",
      size: "670x338",
    };

    if (queryParams) {
      const videoNetworkId = queryParams.get("networkId");
      const videoAdUnit = queryParams.get("adUnit");
      const videoSize = queryParams.get("size");

      if (videoNetworkId || videoAdUnit || videoSize) {
        return {
          enabled: true,
          networkId: videoNetworkId || baseConfig.networkId,
          adUnit: videoAdUnit || baseConfig.adUnit,
          size: videoSize || baseConfig.size,
        };
      }
    }

    return baseConfig;
  }

  // Default: no video ads
  return { enabled: false };
};

const getPlatformConfig = (
  platform: string,
  queryParams?: URLSearchParams,
): AdsConfig => {
  return {
    video: getVideoConfig(platform, queryParams),
    display: getDisplayConfig(platform),
  };
};

const AdsContext = createContext<AdsContextValue>({
  imaStatus: "loading",
  ima: null,
  gptStatus: "loading",
  gpt: null,
});

export const AdsProvider = ({ children }: { children: ReactNode }) => {
  const [imaStatus, setImaStatus] = useState<AdsStatus>("loading");
  const [gptStatus, setGptStatus] = useState<AdsStatus>("loading");
  const [ima, setIma] = useState<object | null>(null);
  const [gpt, setGpt] = useState<typeof window.googletag | null>(null);

  useEffect(() => {
    if (window.google?.ima) {
      setImaStatus("ready");
      setIma(window.google.ima);
    }

    if (window.googletag) {
      setGptStatus("ready");
      setGpt(window.googletag);
    }

    const imaScript = document.createElement("script");
    imaScript.src = "https://imasdk.googleapis.com/js/sdkloader/ima3.js";
    imaScript.async = true;
    document.head.appendChild(imaScript);

    const gptScript = document.createElement("script");
    gptScript.src = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
    gptScript.async = true;
    document.head.appendChild(gptScript);

    let checks = 0;
    const checkInterval = setInterval(() => {
      if (imaStatus === "loading" && window.google?.ima) {
        setImaStatus("ready");
        setIma(window.google.ima);
      }

      if (gptStatus === "loading" && window.googletag) {
        const gt = window.googletag;

        gt.cmd.push(function () {
          gt.enableServices();
        });
        setGptStatus("ready");
        setGpt(window.googletag);
      }

      // Timeout after 2 seconds (200 checks * 10ms)
      if ((imaStatus === "ready" && gptStatus === "ready") || checks > 200) {
        clearInterval(checkInterval);
        if (imaStatus === "loading") setImaStatus("error");
        if (gptStatus === "loading") setGptStatus("error");
      }

      checks++;
    }, 10);

    return () => clearInterval(checkInterval);
  }, [imaStatus, gptStatus]);

  return (
    <AdsContext.Provider value={{ imaStatus, ima, gptStatus, gpt }}>
      {children}
    </AdsContext.Provider>
  );
};

export function useAds(): AdsContextValue & { config: AdsConfig } {
  const context = useContext(AdsContext);
  if (!context) {
    throw new Error("useAds must be used within an AdsProvider");
  }

  const config = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const platform = params.get("platform") ?? "ALLSTAR";
    return getPlatformConfig(platform, params);
  }, []);

  return { ...context, config };
}
